/**
 * Admin Review Store
 *
 * Manages one review at a time (the one open in the deliverable modal).
 */
import { defineStore } from 'pinia';
import { ref, shallowRef, computed } from 'vue';
import { reviewService } from '@/services/reviewService';
import { deliverableService } from '@/services/deliverableService';
import { useUserAuthStore } from '@/stores/userAuthStore';
import { logger } from '@/services/loggerService';
import { detectAssetType, isFileTypeBlocked } from '@/services/assetUploadService';
import { attachStallRecovery } from '@/services/uploadStallRecovery';
import { toastBridge } from '@/services/toastBridge';
import { REVIEW_ITEM_STATUS, FINALIZATION_STATUS } from '@/utils/reviewUtils';
import { DELIVERABLE_STAGES, DELIVERABLE_STATUSES } from '@/utils/deliverableUtils';
import { episodeService } from '@/services/episodeService';
import { db } from '@/js/firebase';
import { doc, getDoc } from 'firebase/firestore';

export const useAdminReviewStore = defineStore('adminReview', () => {
  // ─── External Stores ───────────────────────────────────────
  const userAuthStore = useUserAuthStore();

  // ─── State ───────────────────────────────────────────────

  const review = ref(null);
  /** shallowRef: large collection replaced wholesale via spread pattern */
  const items = shallowRef({});
  /** shallowRef: large collection replaced wholesale via spread/map */
  const comments = shallowRef([]);
  const loading = ref(false);
  const initialized = ref(false);
  const subscribedReviewId = ref(null);
  const error = ref(null);
  const activeItemId = ref(null);
  const activeVersionIds = ref({});

  // Upload queue state
  const uploadQueue = ref([]);
  const isUploading = ref(false);

  // ─── Getters ─────────────────────────────────────────────

  const sortedItems = computed(() =>
    Object.values(items.value).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
  );

  const activeItem = computed(() => {
    if (!activeItemId.value) return null;
    return items.value[activeItemId.value] || null;
  });

  const activeVersion = computed(() => {
    const item = activeItem.value;
    if (!item) return null;
    const versionId = activeVersionIds.value[item.id];
    if (versionId) {
      return item.versions?.find(v => v.id === versionId) || item.versions?.[0] || null;
    }
    return item.versions?.[0] || null;
  });

  const itemComments = computed(() => {
    return (itemId, versionId) => {
      return comments.value.filter(c =>
        c.itemId === itemId && c.versionId === versionId
      );
    };
  });

  const unresolvedCommentCount = computed(() =>
    comments.value.filter(c => !c.parentId && !c.resolved).length
  );

  const reviewProgress = computed(() => {
    if (!review.value) return { approved: 0, pending: 0, changesRequested: 0, total: 0 };
    const statusField = 'clientStatus';
    const allItems = Object.values(items.value);

    return {
      approved: allItems.filter(i => i[statusField] === REVIEW_ITEM_STATUS.APPROVED).length,
      pending: allItems.filter(i => i[statusField] === REVIEW_ITEM_STATUS.PENDING_REVIEW).length,
      changesRequested: allItems.filter(i => i[statusField] === REVIEW_ITEM_STATUS.CHANGES_REQUESTED).length,
      inRevision: allItems.filter(i => i[statusField] === REVIEW_ITEM_STATUS.IN_REVISION).length,
      pendingFinalization: allItems.filter(i => i[statusField] === REVIEW_ITEM_STATUS.PENDING_FINALIZATION).length,
      total: allItems.length
    };
  });

  // ─── Actions ─────────────────────────────────────────────

  async function initReview(reviewId) {
    if (!reviewId) return;
    if (subscribedReviewId.value === reviewId) return;

    unsubscribeReview();
    subscribedReviewId.value = reviewId;

    try {
      loading.value = true;
      initialized.value = false;
      error.value = null;

      // `initialized` flips only once the first snapshots land — flipping it
      // synchronously made the loading state unreachable and rendered the
      // "no assets yet" drop zone while the listeners were still connecting.
      let reviewArrived = false;
      let itemsArrived = false;
      const markReady = () => {
        if (!reviewArrived || !itemsArrived) return;
        initialized.value = true;
        loading.value = false;
      };
      const onError = (err) => {
        error.value = err?.message || 'Failed to load review';
        loading.value = false;
      };

      // Subscribe to review doc (a null review still counts as arrived —
      // deliverables without a review must fall through to the drop zone)
      reviewService.subscribeToReview(reviewId, (data) => {
        review.value = data;
        reviewArrived = true;
        markReady();
      }, { onError });

      // Subscribe to items
      reviewService.subscribeToItems(reviewId, (itemsData) => {
        items.value = itemsData;
        // Auto-select first item if none selected
        if (!activeItemId.value && Object.keys(itemsData).length > 0) {
          const sorted = Object.values(itemsData).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
          activeItemId.value = sorted[0]?.id || null;
        }
        itemsArrived = true;
        markReady();
      }, { onError });

      // Subscribe to all comments (admin sees everything)
      reviewService.subscribeToComments(reviewId, (commentsData) => {
        comments.value = commentsData;
      });

      logger.info('[AdminReviewStore] Subscribed', { reviewId });
    } catch (err) {
      error.value = err.message;
      loading.value = false;
      logger.error('[AdminReviewStore] Init failed', { error: err.message, reviewId });
    }
  }

  // Only this store's own listeners — unsubscribeAll() would also tear down the
  // activity timeline's items listener.
  function unsubscribeReview() {
    const id = subscribedReviewId.value;
    if (!id) return;
    reviewService.unsubscribe(`review_${id}`);
    reviewService.unsubscribe(`items_${id}`);
    reviewService.unsubscribe(`comments_${id}`);
    subscribedReviewId.value = null;
  }

  function reset() {
    unsubscribeReview();
    review.value = null;
    items.value = {};
    comments.value = [];
    loading.value = false;
    initialized.value = false;
    error.value = null;
    activeItemId.value = null;
    activeVersionIds.value = {};
    uploadQueue.value = [];
    isUploading.value = false;
  }

  function selectItem(itemId) {
    activeItemId.value = itemId;
  }

  function selectVersion(itemId, versionId) {
    activeVersionIds.value = { ...activeVersionIds.value, [itemId]: versionId };
  }

  // ─── CRUD Actions ────────────────────────────────────────

  async function createReview(deliverableId, projectId, companyId) {
    try {
      loading.value = true;
      await reviewService.createReview(deliverableId, projectId, companyId);
      await initReview(deliverableId);
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  // The thumbnail slot is a reviewable item AND the episode's display thumbnail
  // (project.episodeThumbnailPath, re-signed at render by BunnyImage). Every
  // upload path funnels through these wrappers, so this is the one place the
  // pointer follows the newest version. Path only — no library `assets` doc,
  // which would leak into the client library before approval. Best-effort.
  function syncEpisodeThumbnail(slot, version) {
    const path = version?.bunnyStorage?.path;
    const projectId = review.value?.projectId;
    if (slot !== 'thumbnail' || !path || !projectId) return;
    episodeService.updateEpisode(projectId, {
      episodeThumbnailPath: path,
      episodeThumbnailUrl: '',
      episodeThumbnailAssetId: '',
    }).catch((err) => logger.warn('[AdminReview] Episode thumbnail pointer sync failed', { projectId, error: err.message }));
  }

  async function addItem(itemData, file, companyId, onProgress, onTask) {
    if (!review.value) return;
    const sortOrder = Object.keys(items.value).length;
    const result = await reviewService.addItem(review.value.id, { ...itemData, sortOrder }, file, companyId, onProgress, onTask);
    syncEpisodeThumbnail(result?.slot, result?.versions?.[0]);
    return result;
  }

  async function addVersion(itemId, file, companyId, note, onProgress, onTask) {
    if (!review.value) return;
    const before = items.value[itemId];
    const wasClientVisible = before?.internalStatus === REVIEW_ITEM_STATUS.APPROVED &&
      before.versions?.find(v => v.id === before.currentVersionId)?.clientVersionNumber != null;
    const result = await reviewService.addVersion(review.value.id, itemId, file, companyId, note, onProgress, onTask);
    syncEpisodeThumbnail(items.value[itemId]?.slot, result);

    // Optimistic update: mirrors reviewService.addVersion — a new version over a
    // client-visible one needs internal re-approval before it's promoted.
    if (wasClientVisible && before.finalizationStatus !== FINALIZATION_STATUS.PENDING) {
      items.value = {
        ...items.value,
        [itemId]: {
          ...items.value[itemId],
          internalStatus: REVIEW_ITEM_STATUS.PENDING_REVIEW,
          internalApprovedBy: null,
          internalApprovedAt: null
        }
      };
    }

    // Optimistic update: if internal changes were requested, transition to in_revision
    if (items.value[itemId]?.internalStatus === REVIEW_ITEM_STATUS.CHANGES_REQUESTED) {
      items.value = {
        ...items.value,
        [itemId]: {
          ...items.value[itemId],
          internalStatus: REVIEW_ITEM_STATUS.IN_REVISION,
          internalApprovedBy: null,
          internalApprovedAt: null
        }
      };
    }

    return result;
  }

  async function replaceVersionFile(itemId, versionId, file, companyId, onProgress, onTask) {
    if (!review.value) return;
    const item = items.value[itemId];
    const result = await reviewService.replaceVersionFile(review.value.id, itemId, versionId, file, companyId, onProgress, onTask);
    // Replacing a non-current version doesn't change what the episode displays.
    if (item?.currentVersionId === versionId) syncEpisodeThumbnail(item?.slot, result);
    return result;
  }

  async function deleteItem(itemId, companyId) {
    if (!review.value) return;
    const deleted = items.value[itemId];
    await reviewService.deleteItem(review.value.id, itemId, companyId);
    if (deleted?.slot === 'thumbnail' && review.value.projectId) {
      clearEpisodeThumbnailIfOwned(review.value.projectId, deleted).catch((err) =>
        logger.warn('[AdminReview] Episode thumbnail pointer clear failed', { error: err.message }));
    }
    if (activeItemId.value === itemId) {
      const remaining = sortedItems.value.filter(i => i.id !== itemId);
      activeItemId.value = remaining[0]?.id || null;
    }
  }

  // Only clear the pointer if it still points at one of the deleted item's
  // files — an EpisodeMetadataPanel or client-provided upload must survive.
  async function clearEpisodeThumbnailIfOwned(projectId, item) {
    const snap = await getDoc(doc(db, 'projects', projectId));
    const current = snap.data()?.episodeThumbnailPath;
    const owned = (item.versions || []).some((v) => v.bunnyStorage?.path && v.bunnyStorage.path === current);
    if (!owned) return;
    await episodeService.updateEpisode(projectId, {
      episodeThumbnailPath: '',
      episodeThumbnailUrl: '',
      episodeThumbnailAssetId: '',
    });
  }

  async function addComment(commentData) {
    if (!review.value) return;
    return reviewService.addComment(review.value.id, commentData);
  }

  async function addReply(parentCommentId, replyText) {
    if (!review.value) {
      logger.warn('[AdminReviewStore] addReply: no active review');
      return;
    }
    const parent = comments.value.find(c => c.id === parentCommentId);
    if (!parent) {
      logger.warn('[AdminReviewStore] addReply: parent not found', { parentCommentId });
      return;
    }
    return reviewService.addComment(review.value.id, {
      itemId: parent.itemId,
      versionId: parent.versionId,
      text: replyText,
      parentId: parentCommentId
    });
  }

  async function updateComment(commentId, updates) {
    if (!review.value) return;
    return reviewService.updateComment(review.value.id, commentId, updates);
  }

  async function deleteComment(commentId) {
    if (!review.value) return;
    return reviewService.deleteComment(review.value.id, commentId);
  }

  async function toggleResolved(commentId, resolved) {
    if (!review.value) return;
    const userId = userAuthStore.user.id;

    // Optimistic local update for instant UI feedback
    comments.value = comments.value.map(c =>
      c.id === commentId
        ? { ...c, resolved, resolvedBy: resolved ? userId : null, resolvedAt: resolved ? new Date() : null }
        : c
    );

    return reviewService.toggleCommentResolved(review.value.id, commentId, resolved, userId);
  }

  async function updateItemFields(itemId, updates) {
    if (!review.value) return;

    // Optimistic local update
    if (items.value[itemId]) {
      items.value = {
        ...items.value,
        [itemId]: { ...items.value[itemId], ...updates }
      };
    }

    return reviewService.updateItem(review.value.id, itemId, updates);
  }

  async function approveItem(itemId, statusType = 'client') {
    if (!review.value) return;
    const userId = userAuthStore.user.id;

    // Optimistic local update
    if (items.value[itemId]) {
      const statusField = statusType === 'internal' ? 'internalStatus' : 'clientStatus';
      const approvedByField = statusType === 'internal' ? 'internalApprovedBy' : 'clientApprovedBy';
      const approvedAtField = statusType === 'internal' ? 'internalApprovedAt' : 'clientApprovedAt';

      const item = items.value[itemId];
      let updatedVersions = item.versions;

      // Optimistic version promotion on internal approval
      if (statusType === 'internal') {
        const currentVersion = item.versions?.find(v => v.id === item.currentVersionId);
        if (currentVersion && currentVersion.clientVersionNumber == null) {
          const maxClientVersion = (item.versions || []).reduce((max, v) => {
            return (v.clientVersionNumber != null && v.clientVersionNumber > max) ? v.clientVersionNumber : max;
          }, 0);
          const nextClientVersion = maxClientVersion + 1;
          updatedVersions = item.versions.map(v => {
            if (v.id === item.currentVersionId) {
              return { ...v, clientVersionNumber: nextClientVersion };
            }
            return v;
          });
        }
      }

      // Finalization-aware optimistic update for internal approval
      const finalizationUpdates = {};
      if (statusType === 'internal' && item.finalizationStatus === FINALIZATION_STATUS.PENDING) {
        finalizationUpdates.finalizationStatus = FINALIZATION_STATUS.COMPLETE;
        finalizationUpdates.clientStatus = REVIEW_ITEM_STATUS.PENDING_REVIEW;
        finalizationUpdates.clientApprovedBy = null;
        finalizationUpdates.clientApprovedAt = null;
      }

      items.value = {
        ...items.value,
        [itemId]: {
          ...items.value[itemId],
          [statusField]: REVIEW_ITEM_STATUS.APPROVED,
          [approvedByField]: userId,
          [approvedAtField]: new Date(),
          versions: updatedVersions,
          ...finalizationUpdates
        }
      };
    }

    return reviewService.updateItemStatus(review.value.id, itemId, statusType, REVIEW_ITEM_STATUS.APPROVED, userId);
  }

  async function requestChanges(itemId, statusType = 'client') {
    if (!review.value) return;
    const userId = userAuthStore.user.id;

    // Optimistic local update
    if (items.value[itemId]) {
      const statusField = statusType === 'internal' ? 'internalStatus' : 'clientStatus';
      const approvedByField = statusType === 'internal' ? 'internalApprovedBy' : 'clientApprovedBy';
      const approvedAtField = statusType === 'internal' ? 'internalApprovedAt' : 'clientApprovedAt';
      const cascadeUpdates = {};
      // Cascade: client changes_requested resets internal to in_revision
      if (statusType === 'client') {
        cascadeUpdates.internalStatus = REVIEW_ITEM_STATUS.IN_REVISION;
        cascadeUpdates.internalApprovedBy = null;
        cascadeUpdates.internalApprovedAt = null;
      }
      items.value = {
        ...items.value,
        [itemId]: {
          ...items.value[itemId],
          [statusField]: REVIEW_ITEM_STATUS.CHANGES_REQUESTED,
          [approvedByField]: null,
          [approvedAtField]: null,
          changesRequestedAtVersion: items.value[itemId].currentVersionNumber || 1,
          ...cascadeUpdates
        }
      };
    }

    return reviewService.updateItemStatus(review.value.id, itemId, statusType, REVIEW_ITEM_STATUS.CHANGES_REQUESTED, userId, {
      changesRequestedAtVersion: items.value[itemId]?.currentVersionNumber || 1
    });
  }

  async function resetStatus(itemId, statusType = 'client') {
    if (!review.value) return;
    const userId = userAuthStore.user.id;

    // Build extra updates for a single atomic write (avoids race between
    // sequential updateItemStatus + demoteVersionFromClient calls)
    const extraUpdates = {};

    if (items.value[itemId]) {
      const item = items.value[itemId];
      const statusField = statusType === 'internal' ? 'internalStatus' : 'clientStatus';
      const approvedByField = statusType === 'internal' ? 'internalApprovedBy' : 'clientApprovedBy';
      const approvedAtField = statusType === 'internal' ? 'internalApprovedAt' : 'clientApprovedAt';

      // On internal unapprove: strip clientVersionNumber and reset clientStatus.
      // The actual versions[] mutation is applied server-side on a fresh read
      // (extraUpdates.demoteCurrentVersion) so it can't clobber a concurrent
      // Bunny webhook write; updatedVersions here is only for the optimistic
      // local update below.
      let updatedVersions = item.versions;
      if (statusType === 'internal' && item.versions) {
        const currentVersion = item.versions.find(v => v.id === item.currentVersionId);
        if (currentVersion?.clientVersionNumber != null) {
          updatedVersions = item.versions.map(v =>
            v.id === item.currentVersionId ? { ...v, clientVersionNumber: null } : v
          );
          extraUpdates.demoteCurrentVersion = true;
          extraUpdates.clientStatus = REVIEW_ITEM_STATUS.PENDING_REVIEW;
          extraUpdates.clientApprovedBy = null;
          extraUpdates.clientApprovedAt = null;
        }
      }

      items.value = {
        ...items.value,
        [itemId]: {
          ...item,
          versions: updatedVersions,
          [statusField]: REVIEW_ITEM_STATUS.PENDING_REVIEW,
          [approvedByField]: null,
          [approvedAtField]: null,
          ...(extraUpdates.clientStatus ? {
            clientStatus: extraUpdates.clientStatus,
            clientApprovedBy: null,
            clientApprovedAt: null
          } : {})
        }
      };
    }

    // Single write: status reset + version demotion in one updateDoc
    return reviewService.updateItemStatus(review.value.id, itemId, statusType, REVIEW_ITEM_STATUS.PENDING_REVIEW, userId, extraUpdates);
  }

  async function resubmitForReview(statusType = 'internal') {
    if (!review.value) return;
    const userId = userAuthStore.user.id;
    const statusField = statusType === 'internal' ? 'internalStatus' : 'clientStatus';

    // Optimistic update: reset changes_requested items to pending_review
    const updatedItems = {};
    for (const [id, item] of Object.entries(items.value)) {
      if (item[statusField] === REVIEW_ITEM_STATUS.CHANGES_REQUESTED || item[statusField] === REVIEW_ITEM_STATUS.IN_REVISION) {
        updatedItems[id] = {
          ...item,
          [statusField]: REVIEW_ITEM_STATUS.PENDING_REVIEW,
          changesRequestedAtVersion: null
        };
      } else {
        updatedItems[id] = item;
      }
    }
    items.value = updatedItems;

    return reviewService.resubmitForInternalReview(review.value.id, statusType, userId);
  }

  async function resetReviewStatus(statusType = 'internal') {
    if (!review.value) return;
    const userId = userAuthStore.user.id;
    const statusField = statusType === 'internal' ? 'internalStatus' : 'clientStatus';

    // Optimistic update: reset all items to pending_review
    const updatedItems = {};
    for (const [id, item] of Object.entries(items.value)) {
      if (item[statusField] && item[statusField] !== REVIEW_ITEM_STATUS.PENDING_REVIEW) {
        updatedItems[id] = {
          ...item,
          [statusField]: REVIEW_ITEM_STATUS.PENDING_REVIEW,
          ...(statusType === 'internal'
            ? { internalApprovedBy: null, internalApprovedAt: null }
            : { clientApprovedBy: null, clientApprovedAt: null }),
          changesRequestedAtVersion: null
        };
      } else {
        updatedItems[id] = item;
      }
    }
    items.value = updatedItems;

    return reviewService.resetReviewStatus(review.value.id, statusType, userId);
  }

  async function setBulkReviewStatus(statusType, newStatus, options = {}) {
    if (!review.value) return;
    if (newStatus === REVIEW_ITEM_STATUS.PENDING_REVIEW) {
      return resetReviewStatus(statusType);
    }

    const userId = userAuthStore.user.id;
    const statusField = statusType === 'internal' ? 'internalStatus' : 'clientStatus';
    const approvedByField = statusType === 'internal' ? 'internalApprovedBy' : 'clientApprovedBy';
    const approvedAtField = statusType === 'internal' ? 'internalApprovedAt' : 'clientApprovedAt';

    // Optimistic update: set all items to the new status
    const updatedItems = {};
    for (const [id, item] of Object.entries(items.value)) {
      updatedItems[id] = {
        ...item,
        [statusField]: newStatus,
        [approvedByField]: newStatus === REVIEW_ITEM_STATUS.APPROVED ? userId : null,
        [approvedAtField]: newStatus === REVIEW_ITEM_STATUS.APPROVED ? new Date() : null,
        ...(newStatus === REVIEW_ITEM_STATUS.APPROVED ? { changesRequestedAtVersion: null } : {}),
        ...(newStatus === REVIEW_ITEM_STATUS.CHANGES_REQUESTED ? { changesRequestedAtVersion: item.currentVersionNumber || 1 } : {}),
        // Cascade: client changes_requested resets internal approval
        ...(statusType === 'client' && newStatus === REVIEW_ITEM_STATUS.CHANGES_REQUESTED
          ? { internalStatus: REVIEW_ITEM_STATUS.IN_REVISION, internalApprovedBy: null, internalApprovedAt: null }
          : {})
      };
    }
    items.value = updatedItems;

    return reviewService.setBulkReviewStatus(review.value.id, statusType, newStatus, userId, options);
  }

  async function reorderItems(itemUpdates) {
    if (!review.value) return;
    return reviewService.reorderItems(review.value.id, itemUpdates);
  }

  // Promote every item whose current version hasn't been promoted to client yet.
  // Recovery path for the "frozen client view" pattern where the deliverable
  // moved to client_review (e.g. via stage automation) without each item's
  // current version being promoted via the per-item approve button.
  async function promoteUnpromotedItems() {
    if (!review.value) return { promoted: 0 };

    const unpromoted = Object.values(items.value).filter(item => {
      if (item.finalizationStatus === 'pending') return false;
      const currentVersion = item.versions?.find(v => v.id === item.currentVersionId);
      return currentVersion && currentVersion.clientVersionNumber == null;
    });

    let promoted = 0;
    for (const item of unpromoted) {
      try {
        const result = await reviewService.promoteVersionToClient(review.value.id, item.id);
        if (result?.promoted) promoted++;
      } catch {
        // Per-item failures are logged in reviewService; continue with the rest
        continue;
      }
    }
    return { promoted, attempted: unpromoted.length };
  }

  // Automation rules suppressed during this flow. The IDs here correspond to
  // rules that would otherwise fire on the same field changes this action
  // produces (internal approval + stage→client_review) and double-up on the
  // promotion the action is already performing manually.
  const SKIP_INTERNAL_REVIEW_AUTOMATION_RULE_IDS = ['UDQkw91Sc8FHGcYkZTCk'];

  // Skip the internal review phase entirely: bulk-resolve comments (if requested),
  // bulk-approve every item internally so the auto-promote trigger fires, then
  // explicitly write stage = client_review and status = ready on the deliverable.
  async function skipInternalReview({ resolveUnresolvedComments = false } = {}) {
    if (!review.value) return { skipped: false };
    const allItems = Object.values(items.value);
    if (allItems.length === 0) return { skipped: false };

    const userId = userAuthStore.user.id;
    const reviewId = review.value.id;
    let resolvedCount = 0;

    try {
      if (resolveUnresolvedComments) {
        const result = await reviewService.bulkResolveAllComments(reviewId, userId);
        resolvedCount = result?.resolved ?? 0;
        if (resolvedCount > 0) {
          // Optimistic update: mark every comment resolved locally so the UI
          // doesn't briefly show "unresolved" mid-flight.
          comments.value = comments.value.map(c => ({
            ...c,
            resolved: true,
            resolvedBy: userId,
            resolvedAt: c.resolvedAt || new Date()
          }));
        }
      }

      await setBulkReviewStatus('internal', REVIEW_ITEM_STATUS.APPROVED, {
        skipRuleIds: SKIP_INTERNAL_REVIEW_AUTOMATION_RULE_IDS
      });

      await deliverableService.updateDeliverable(reviewId, {
        stage: DELIVERABLE_STAGES.CLIENT_REVIEW,
        status: DELIVERABLE_STATUSES.READY
      }, false, {
        skipRuleIds: SKIP_INTERNAL_REVIEW_AUTOMATION_RULE_IDS
      });

      logger.info('[AdminReviewStore] Skipped internal review', {
        reviewId, items: allItems.length, resolvedComments: resolvedCount
      });
      toastBridge.success(`Sent to client review (${allItems.length} item${allItems.length === 1 ? '' : 's'}${resolvedCount > 0 ? `, ${resolvedCount} comment${resolvedCount === 1 ? '' : 's'} resolved` : ''})`);

      return { skipped: true, items: allItems.length, resolvedComments: resolvedCount };
    } catch (error) {
      logger.error('[AdminReviewStore] Failed to skip internal review', { error: error.message, reviewId });
      toastBridge.error('Failed to skip internal review');
      throw error;
    }
  }

  // ─── Upload Queue Actions ──────────────────────────────

  let queueIdCounter = 0;
  // Non-reactive: holds the live Firebase uploadTask per queue item id so the
  // UI can pause/resume it. Kept out of reactive state to avoid proxying the task.
  const queueUploadTasks = new Map();

  function _reviewFileType(file) {
    const assetType = detectAssetType(file);
    if (['videos', 'selects', 'social'].includes(assetType)) return 'video';
    if (['photography', 'graphics'].includes(assetType)) return 'image';
    if (assetType === 'audio') return 'audio';
    return 'document';
  }

  function enqueueFiles(files, slot = null) {
    for (const file of files) {
      const { blocked, reason } = isFileTypeBlocked(file);
      if (blocked) {
        toastBridge.warning(reason);
        continue;
      }
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      uploadQueue.value.push({
        id: `q_${++queueIdCounter}`,
        file,
        title: nameWithoutExt,
        type: _reviewFileType(file),
        mimeType: file.type,
        slot, // podcast slot key (e.g. 'full_episode'); null for free-form reviews
        status: 'queued',
        progress: 0,
        error: null
      });
    }
  }

  function updateQueueItemTitle(id, title) {
    const item = uploadQueue.value.find(q => q.id === id);
    if (item) item.title = title;
  }

  function removeQueueItem(id) {
    const idx = uploadQueue.value.findIndex(q => q.id === id);
    // Allow removing not-yet-started items and failed items (so the user can
    // dismiss a failed upload); never yank one mid-upload.
    if (idx !== -1 && ['queued', 'error'].includes(uploadQueue.value[idx].status)) {
      uploadQueue.value.splice(idx, 1);
    }
  }

  function pauseQueueItem(id) {
    const task = queueUploadTasks.get(id);
    const item = uploadQueue.value.find(q => q.id === id);
    if (task && item && item.status === 'uploading') {
      task.pause();
      item.status = 'paused';
    }
  }

  function resumeQueueItem(id) {
    const task = queueUploadTasks.get(id);
    const item = uploadQueue.value.find(q => q.id === id);
    if (task && item && item.status === 'paused') {
      task.resume();
      item.status = 'uploading';
    }
  }

  function retryQueueItem(id) {
    const item = uploadQueue.value.find(q => q.id === id);
    // Re-arm a failed item; the caller re-runs processUploadQueue to pick it up.
    if (item && item.status === 'error') {
      item.status = 'queued';
      item.error = null;
      item.progress = 0;
    }
  }

  async function processUploadQueue(deliverableId, projectId, companyId) {
    if (isUploading.value || uploadQueue.value.length === 0) return;

    isUploading.value = true;

    try {
      // Auto-create review if it doesn't exist yet
      if (!review.value) {
        await createReview(deliverableId, projectId, companyId);
      }

      const queued = uploadQueue.value.filter(q => q.status === 'queued');

      for (const queueItem of queued) {
        queueItem.status = 'uploading';
        queueItem.progress = 0;

        // Stall watchdog — armed once we capture the upload task via onTask below.
        // Starts as a no-op so the onProgress closure can call it unconditionally.
        let stall = { noteProgress: () => {}, cancel: () => {} };

        try {
          await addItem(
            {
              name: queueItem.title,
              type: queueItem.type,
              mimeType: queueItem.mimeType,
              ...(queueItem.slot ? { slot: queueItem.slot } : {}),
              requiresFinalization: queueItem.requiresFinalization || false,
              finalizationNote: queueItem.finalizationNote || null,
            },
            queueItem.file,
            companyId,
            (progress) => { queueItem.progress = Math.round(progress); stall.noteProgress(progress); },
            (task) => {
              queueUploadTasks.set(queueItem.id, task);
              stall = attachStallRecovery(task, {
                // Don't auto-resume while the user has manually paused this item.
                isActive: () => queueItem.status === 'uploading',
                label: `[AdminReviewStore] queue ${queueItem.id}`
              });
            }
          );
          queueItem.status = 'done';
          queueItem.progress = 100;
        } catch (err) {
          const isTimeout = err.message?.includes('retry-limit-exceeded') || err.message?.includes('Max retry time');
          queueItem.status = 'error';
          queueItem.error = isTimeout
            ? 'Upload timed out. Please check your connection and try again.'
            : (err.message || 'Upload failed');
          // Single authoritative log for this failure (service layer logs at warn).
          // Benign network timeouts log at warn (kept out of the error log); genuine
          // failures stay at error.
          if (isTimeout) {
            logger.warn('[AdminReviewStore] Queue item upload timed out', { id: queueItem.id, error: err.message });
          } else {
            logger.error('[AdminReviewStore] Queue item upload failed', { id: queueItem.id, error: err.message });
          }
        } finally {
          stall.cancel();
          queueUploadTasks.delete(queueItem.id);
        }
      }
    } finally {
      isUploading.value = false;
    }
  }

  function clearCompletedFromQueue() {
    // Keep 'error' items so the user can see the failure and retry; only sweep
    // successfully-uploaded items.
    uploadQueue.value = uploadQueue.value.filter(q => q.status !== 'done');
  }

  return {
    // State
    review,
    items,
    comments,
    loading,
    initialized,
    error,
    activeItemId,
    activeVersionIds,
    uploadQueue,
    isUploading,

    // Getters
    sortedItems,
    activeItem,
    activeVersion,
    itemComments,
    unresolvedCommentCount,
    reviewProgress,

    // Actions
    initReview,
    reset,
    selectItem,
    selectVersion,
    createReview,
    addItem,
    addVersion,
    replaceVersionFile,
    deleteItem,
    addComment,
    addReply,
    updateComment,
    deleteComment,
    toggleResolved,
    updateItemFields,
    approveItem,
    requestChanges,
    resetStatus,
    resubmitForReview,
    resetReviewStatus,
    setBulkReviewStatus,
    reorderItems,
    promoteUnpromotedItems,
    skipInternalReview,
    enqueueFiles,
    updateQueueItemTitle,
    removeQueueItem,
    pauseQueueItem,
    resumeQueueItem,
    retryQueueItem,
    processUploadQueue,
    clearCompletedFromQueue
  };
});
